import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp,
  limit
} from '@angular/fire/firestore';

@Injectable({
  providedIn: 'root',
})
export class WorkshopService {
  private firestore = inject(Firestore);

  /* ---------------- CREATE WORKSHOP ---------------- */
  async createWorkshop(data: any) {
    const workshopCol = collection(this.firestore, 'workshops');

    return await addDoc(workshopCol, {
      ...data,
      createdAt: Timestamp.now()
    });
  }

  /* ---------------- GET ALL WORKSHOPS ---------------- */
  async getAllWorkshops() {
    const workshopCol = collection(this.firestore, 'workshops');
    const q = query(workshopCol, orderBy('createdAt', 'desc'));

    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  async getLatestWorkshop() {
    const workshopCol = collection(this.firestore, 'workshops');

    // Order by createdAt descending, limit 1
    const q = query(workshopCol, orderBy('createdAt', 'desc'), limit(1));

    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const docData = snapshot.docs[0];
      const cleanData = this.convertWorkshopTimestamps(docData.data());
      return {
        id: docData.id,
        ...cleanData
      };
    }

    return null; // no workshop found
  }

  async getLatestUpdatedWorkshop() {
    const workshopCol = collection(this.firestore, 'workshops');

    const q = query(workshopCol, orderBy('updatedAt', 'desc'), limit(1));

    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const docData = snapshot.docs[0];
      const cleanData = this.convertWorkshopTimestamps(docData.data());

      return {
        id: docData.id,
        ...cleanData
      }
    }
    return null;
  }

  /* ---------------- GET SINGLE WORKSHOP ---------------- */
  async getWorkshopById(id: string) {
    const workshopRef = doc(this.firestore, `workshops/${id}`);
    const snapshot = await getDoc(workshopRef);

    if (snapshot.exists()) {
      const cleanData = this.convertWorkshopTimestamps(snapshot.data());

      return {
        id: snapshot.id,
        ...cleanData
      };
    }

    return null;
  }

  /* ---------------- UPDATE WORKSHOP ---------------- */
  async updateWorkshop(id: string, data: any) {
    const workshopRef = doc(this.firestore, `workshops/${id}`);

    return await updateDoc(workshopRef, {
      ...data,
      updatedAt: Timestamp.now()
    });
  }

  /* ---------------- DELETE WORKSHOP ---------------- */
  async deleteWorkshop(id: string) {
    const workshopRef = doc(this.firestore, `workshops/${id}`);
    return await deleteDoc(workshopRef);
  }

  private convertWorkshopTimestamps(data: any) {
    return {
      ...data,
      date: data.date?.map((d: any) => d?.toDate?.() ?? d),
      timeFrom: data.timeFrom?.toDate?.() ?? data.timeFrom,
      timeTo: data.timeTo?.toDate?.() ?? data.timeTo,
      createdAt: data.createdAt?.toDate?.() ?? data.createdAt,
      updatedAt: data.updatedAt?.toDate?.() ?? data.updatedAt
    };
  }
}
